// import { lazy } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import lazyPage from 'utils/lazyPage';

// project imports
import MainLayout from 'layout/MainLayout';
import Loadable from 'ui-component/Loadable';
import OrganizationAdditionalEmailPage from 'views/account/organization-profile/OrganizationAdditionalEmail.Page';
import SharedLetterHeadPage from 'views/shared/SharedLetterHead.Page';
import OtherInformations from 'views/account/organization-profile/other-informations';
import ProfileInfoPage from 'views/account/user-profile/ProfileInfo.Page';
import AlertNotificationPage from 'views/account/user-profile/AlertNotification.Page';
import SignaturePage from 'views/account/user-profile/SignaturePage';
import ResetPassword from 'views/account/user-profile/ResetPassword.Page';
import { element } from 'prop-types';
// dashboard routing
const DashboardDefault = Loadable(lazyPage(() => import('views/dashboard/Default')));

const AdditionalUsersPage = Loadable(lazyPage(() => import('views/account/additional-users')));
const UserProfilePage = Loadable(lazyPage(() => import('views/account/user-profile')));
const ManageAccountPage = Loadable(lazyPage(() => import('views/admin/manage-accounts')));
const ManageAnnouncementPage = Loadable(lazyPage(() => import('views/admin/manage-announcements')));
const ManageSubscriptionPlanPage = Loadable(lazyPage(() => import('views/admin/manage-subscription-plans')));
const ManageCouponsPage = Loadable(lazyPage(() => import('views/admin/manage-coupons')));
const ManageFeedbacksPage = Loadable(lazyPage(() => import('views/admin/manage-feedbacks')));
const ManageDeadlinesPage = Loadable(lazyPage(() => import('views/admin/manage-deadlines')));
const ManageSmartPhrasesPage = Loadable(lazyPage(() => import('views/admin/manage-smart-phrases')));
const CreateAccountPage = Loadable(lazyPage(() => import('views/admin/create-account')));
const CreateAnnouncementPage = Loadable(lazyPage(() => import('views/admin/create-announcement')));
const UpdateAnnouncementPage = Loadable(lazyPage(() => import('views/admin/update-announcement')));
const CreateSubscriptionPage = Loadable(lazyPage(() => import('views/admin/create-subscription-plan')));
const UpdateSubscriptionPage = Loadable(lazyPage(() => import('views/admin/update-subscription-plan')));
const UpdateAccountPage = Loadable(lazyPage(() => import('views/admin/update-account')));
const UpdateFoundationCaPage = Loadable(lazyPage(() => import('views/admin/update-foundation-ca')));
const UpdateFoundationUsPage = Loadable(lazyPage(() => import('views/admin/update-foundation-us')));
const ManagePlansPage = Loadable(lazyPage(() => import('views/admin/manage-plans')));
const ManageFoundationsPage = Loadable(lazyPage(() => import('views/admin/manage-foundations')));
const OrganizationProfilePage = Loadable(lazyPage(() => import('views/account/organization-profile')));
const UserProjectPage = Loadable(lazyPage(() => import('views/user-project')));
const UserProjectArchive = Loadable(lazyPage(() => import('views/user-project/UserProject.Archive')));
const SearchByGivingHistoryPage = Loadable(lazyPage(() => import('views/search-engine/by-giving-history')));
const SearchByGivingHistoryPageV2 = Loadable(lazyPage(() => import('views/search-engine/giving-history-v2')));
const SearchByNamePage = Loadable(lazyPage(() => import('views/search-engine/by-name')));
const SearchByNamePageV2 = Loadable(lazyPage(() => import('views/search-engine/by-name-v2')));
const SearchByNamePageCA = Loadable(lazyPage(() => import('views/search-engine-ca/by-name')));
const SearchByNamePageCAV2 = Loadable(lazyPage(() => import('views/search-engine-ca/by-name-v2')));
const SearchByDirectorPage = Loadable(lazyPage(() => import('views/search-engine/by-director')));
const SearchByDirectorPageV2 = Loadable(lazyPage(() => import('views/search-engine/by-director-v2')));
const SearchByYearPage = Loadable(lazyPage(() => import('views/search-engine/by-year')));
const SearchByYearPageV2 = Loadable(lazyPage(() => import('views/search-engine/by-year-v2')));
const SearchByRatingPage = Loadable(lazyPage(() => import('views/search-engine/by-rating')));
const SearchByCustomPage = Loadable(lazyPage(() => import('views/search-engine/by-custom')));
const ProjectBudgetPage = Loadable(lazyPage(() => import('views/project-budget')));
const FoundationProfilePageV2 = Loadable(lazyPage(() => import('views/foundation-profile-v2')));
const InquiryPage = Loadable(lazyPage(() => import('views/document-generator/inquiry')));
const ProposalPage = Loadable(lazyPage(() => import('views/document-generator/proposal')));
const CustomPage = Loadable(lazyPage(() => import('views/document-generator/custom')));
const GrantSecretary = Loadable(lazyPage(() => import('views/grant-secretary')));
const GrantSecretaryV2 = Loadable(lazyPage(() => import('views/grant-secretary-v2')));
const GrantSecretaryPreviewDocuments = Loadable(lazyPage(() => import('views/grant-secretary/GrantSecretary.PreviewDocuments')));
const GrantSecretaryPreviewDocumentsV2 = Loadable(lazyPage(() => import('views/grant-secretary-v2/GrantSecretary.PreviewDocuments')));
const ManageFundersPage = Loadable(lazyPage(() => import('views/manage-funders-v2')));
// const FavoritesPage = Loadable(lazyPage(() => import('views/manage-funders/favorites')));
// const BlockedFundersPage = Loadable(lazyPage(() => import('views/manage-funders/blocked-funders')));
const CustomDatabasesPage = Loadable(lazyPage(() => import('views/manage-funders-v2/custom-databases')));
const CustomDatabasesImportPage = Loadable(lazyPage(() => import('views/manage-funders-v2/custom-databases/CustomDatabase.Import')));
const CustomProfilePage = Loadable(lazyPage(() => import('views/custom-profile')));
const CustomProfilePageV2 = Loadable(lazyPage(() => import('views/custom-profile-v2')));
const CustomProfilePageV3 = Loadable(lazyPage(() => import('views/custom-profile-v3')));
const DocumentInquiryPage = Loadable(lazyPage(() => import('views/document-generator/document-inquiry')));
const DocumentInquiryFunderPage = Loadable(lazyPage(() => import('views/document-generator/document-inquiry/DocumentInquiry.FunderPage')));
const DocumentProposalPage = Loadable(lazyPage(() => import('views/document-generator/document-proposal')));
const DocumentProposalFunderPage = Loadable(lazyPage(() => import('views/document-generator/document-proposal/DocumentProposal.FunderPage')));
const DocumentCustomPage = Loadable(lazyPage(() => import('views/document-generator/document-custom')));
const DocumentCustomFunderPage = Loadable(lazyPage(() => import('views/document-generator/document-custom/DocumentCustom.FunderPage')));
const SearchByKeywordPage = Loadable(lazyPage(() => import('views/search-engine/by-keyword')));
const SearchByKeywordPageV2 = Loadable(lazyPage(() => import('views/search-engine/by-keyword-v2')));
const SearchByDirectorPageCA = Loadable(lazyPage(() => import('views/search-engine-ca/by-director')));
const SearchByDirectorPageCAV2 = Loadable(lazyPage(() => import('views/search-engine-ca/by-director-v2')));
const SearchByGivingHistoryPageCA = Loadable(lazyPage(() => import('views/search-engine-ca/by-giving-history')));
const SearchByGivingHistoryPageCAV2 = Loadable(lazyPage(() => import('views/search-engine-ca/by-giving-history-v2')));
const SearchByYearPageCA = Loadable(lazyPage(() => import('views/search-engine-ca/by-year')));
const SearchByYearPageCAV2 = Loadable(lazyPage(() => import('views/search-engine-ca/by-year-v2')));
const SearchByDeadlinePage = Loadable(lazyPage(() => import('views/search-engine/by-deadline')));
const FoundationProfileCanadianV2 = Loadable(lazyPage(() => import('views/foundation-profile-ca-v2')));
const SearchByInternacionalFundingPage = Loadable(lazyPage(() => import('views/search-engine/by-internacional-funding')));
const SearchByInternacionalFundingPageV2 = Loadable(lazyPage(() => import('views/search-engine/by-internacional-funding-v2')));
const ThankYouLetterPage = Loadable(lazyPage(() => import('views/document-generator/thank-you')));
const DocumentThankYouPage = Loadable(lazyPage(() => import('views/document-generator/document-thank-you')));
const DocumentThankYouFunderPage = Loadable(lazyPage(() => import('views/document-generator/document-thank-you/DocumentThankYou.FunderPage')));
const ImpactReportLetterPage = Loadable(lazyPage(() => import('views/document-generator/impact-report')));
const DocumentImpactReportPage = Loadable(lazyPage(() => import('views/document-generator/document-impact-report')));
const DocumentImpactReportFunderPage = Loadable(lazyPage(() => import('views/document-generator/document-impact-report/DocumentImpactReport.FunderPage')));
const DocumentOptimizationPage = Loadable(lazyPage(() => import('views/document-generator/common/document-optimization')));
const SearchByCustomPageV2 = Loadable(lazyPage(() => import('views/search-engine/by-custom-v2')));
const SearchByCustomProfilePage = Loadable(lazyPage(() => import('views/search-engine/by-custom-profile')));
const NewDocumentGenerator = Loadable(lazyPage(() => import('views/document-generator-v2')));
const DocumentBuilder = Loadable(lazyPage(() => import('views/document-generator-v2/DocumentBuilder')));
const NewDocumentOptimization = Loadable(lazyPage(() => import('views/document-generator-v2/document-optimization')));
const SubscriptionInfoPage = Loadable(lazyPage(() => import('views/account/organization-profile/subscription-info')));
const SearchEngineV3 = Loadable(lazyPage(() => import('views/search-engine/v3')));

// permanently redirects the retired /foundation-profile pages to their -v2 equivalents,
// preserving the profile id, query string (needed by the qualify next/previous flow) and hash
const FoundationProfileRedirect = () => {
    const { profileId } = useParams();
    const location = useLocation();
    return <Navigate to={`/main/foundation-profile-v2/${profileId}${location.search}${location.hash}`} replace />;
};

const FoundationProfileCaRedirect = () => {
    const { profileId } = useParams();
    const location = useLocation();
    return <Navigate to={`/main/foundation-profile-ca-v2/${profileId}${location.search}${location.hash}`} replace />;
};

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
    path: '/main',
    element: <MainLayout />,
    children: [
        {
            path: '/',
            element: <DashboardDefault />
        },
        {
            path: '/dashboard/default',
            element: <DashboardDefault />
        },
        {
            path: '/admin/manage-accounts',
            element: <ManageAccountPage />
        },
        {
            path: '/admin/manage-announcements',
            element: <ManageAnnouncementPage />
        },
        {
            path: '/admin/manage-subscription-plans',
            element: <ManageSubscriptionPlanPage />
        },
        {
            path: '/admin/manage-plans',
            element: <ManagePlansPage />
        },
        {
            path: '/admin/create-account',
            element: <CreateAccountPage />
        },
        {
            path: '/admin/create-announcement',
            element: <CreateAnnouncementPage />
        },
        {
            path: '/admin/update-announcement/:id',
            element: <UpdateAnnouncementPage />
        },
        {
            path: '/admin/create-subscription-plan',
            element: <CreateSubscriptionPage />
        },
        {
            path: '/admin/manage-coupons',
            element: <ManageCouponsPage />
        },
        {
            path: '/admin/manage-feedbacks',
            element: <ManageFeedbacksPage />
        },
        {
            path: '/admin/manage-deadlines',
            element: <ManageDeadlinesPage />
        },
        {
            path: '/admin/manage-smart-phrases',
            element: <ManageSmartPhrasesPage />
        },
        {
            path: '/admin/update-subscription-plan/:subscriptionPlanId',
            element: <UpdateSubscriptionPage />
        },
        {
            path: '/admin/update-account/:userId',
            element: <UpdateAccountPage />
        },
        {
            path: '/admin/update-foundation-ca/:profileId',
            element: <UpdateFoundationCaPage />
        },
        {
            path: '/admin/update-foundation-us/:profileId',
            element: <UpdateFoundationUsPage />
        },
        {
            path: '/admin/manage-foundations',
            element: <ManageFoundationsPage />
        },
        {
            path: '/account/user-profile',
            element: <UserProfilePage />,
            children: [
                {
                    index: true,
                    element: <ProfileInfoPage />,
                },
                {
                    path: '/profile-info',
                    element: <ProfileInfoPage />,
                },
                {
                    path: '/email-alert',
                    element: <AlertNotificationPage />,
                },
                {
                    path: '/signature',
                    element: <SignaturePage />,
                },
                {
                    path: '/letter-head',
                    element: <SharedLetterHeadPage sharedLetterHeadType="user" />,
                },
                {
                    path: "/password-reset",
                    element: <ResetPassword />
                },
            ]
        },
        {
            path: '/account/additional-users',
            element: <AdditionalUsersPage />
        },
        {
            path: '/account/organization-profile',
            element: <OrganizationProfilePage />,
            children: [
                {
                    index: true,
                    element: <OtherInformations />,
                },
                {
                    path: '/profile-info',
                    element: <OtherInformations />,
                },
                {
                    path: '/additional-email',
                    element: <OrganizationAdditionalEmailPage />,
                },
                {
                    path: '/letter-head',
                    element: <SharedLetterHeadPage sharedLetterHeadType="organization" />,
                },
                {
                    path: '/subscription-info',
                    element: <SubscriptionInfoPage />,
                }
            ]
        },
        {
            path: '/user-project/list',
            element: <UserProjectPage />
        },
        {
            path: '/user-project/archive',
            element: <UserProjectArchive />
        },
        {
            path: '/search/by-giving-history',
            element: <SearchByGivingHistoryPage />
        },
        {
            path: '/search/giving-history-v2',
            element: <SearchByGivingHistoryPageV2 />
        },
        {
            path: '/search/by-name',
            element: <SearchByNamePage />
        },
        {
            path: '/search/by-name-v2',
            element: <SearchByNamePageV2 />
        },
        {
            path: '/search/by-director',
            element: <SearchByDirectorPage />
        },
        {
            path: '/search/by-director-v2',
            element: <SearchByDirectorPageV2 />
        },
        {
            path: '/search/by-year',
            element: <SearchByYearPage />
        },
        {
            path: '/search/by-year-v2',
            element: <SearchByYearPageV2 />
        },
        {
            path: '/search/by-rating',
            element: <SearchByRatingPage />
        },

        {
            path: '/search/by-custom',
            element: <SearchByCustomPage />
        },
        {
            path: '/search/by-custom-v2',
            element: <SearchByCustomPageV2 />
        },
        {
            path: '/search/by-custom-profile',
            element: <SearchByCustomProfilePage />
        },
        {
            path: '/search/new',
            element: <SearchEngineV3 />
        },
        {
            path: '/project-budget/:userProjectId',
            element: <ProjectBudgetPage />
        },
        {
            path: '/foundation-profile/:profileId',
            element: <FoundationProfileRedirect />
        },
        {
            path: '/foundation-profile-v2/:profileId',
            element: <FoundationProfilePageV2 />
        },
        {
            path: '/document-generator/inquiry',
            element: <InquiryPage />
        },
        {
            path: '/document-generator/proposal',
            element: <ProposalPage />
        },
        {
            path: '/document-generator/custom-letter',
            element: <CustomPage />
        },
        {
            path: '/legacy-grant-secretary',
            element: <GrantSecretary />
        },
        {
            path: '/grant-secretary',
            element: <GrantSecretaryV2 />
        },
        {
            path: '/legacy-grant-secretary/preview-documents',
            element: <GrantSecretaryPreviewDocuments />
        },
        {
            path: '/grant-secretary/preview-documents',
            element: <GrantSecretaryPreviewDocumentsV2 />
        },
        {
            path: '/manage-funders',
            element: <ManageFundersPage />
        },
        {
            path: '/manage-funders/:tabType',
            element: <ManageFundersPage />
        },
        // {
        //     path: '/manage-funders/favorites',
        //     element: <FavoritesPage />
        // },
        // {
        //     path: '/manage-funders/blocked-funders',
        //     element: <BlockedFundersPage />
        // },
        {
            path: '/manage-funders/custom-databases',
            element: <CustomDatabasesPage />
        },
        {
            path: '/manage-funders/custom-databases/import',
            element: <CustomDatabasesImportPage />
        },
        {
            path: '/custom-profile/:customItemId',
            element: <CustomProfilePage />
        },
        {
            path: '/custom-profile-v2/:customItemId',
            element: <CustomProfilePageV2 />
        },
        {
            path: '/custom-profile-v3/:customItemId',
            element: <CustomProfilePageV3 />
        },
        {
            path: '/document-generator/document-inquiry',
            element: <DocumentInquiryPage />
        },
        {
            path: '/document-generator/document-inquiry/funder/:profileId',
            element: <DocumentInquiryFunderPage />
        },
        {
            path: '/document-generator/document-proposal',
            element: <DocumentProposalPage />
        },
        {
            path: '/document-generator/document-proposal/funder/:profileId',
            element: <DocumentProposalFunderPage />
        },
        {
            path: '/document-generator/document-custom',
            element: <DocumentCustomPage />
        },
        {
            path: '/document-generator/document-custom/funder/:profileId',
            element: <DocumentCustomFunderPage />
        },
        {
            path: '/search/by-keyword',
            element: <SearchByKeywordPage />
        },
        {
            path: '/search/by-keyword-v2',
            element: <SearchByKeywordPageV2 />
        },
        {
            path: '/search/name-ca',
            element: <SearchByNamePageCA />
        },
        {
            path: '/search/name-ca-v2',
            element: <SearchByNamePageCAV2 />
        },
        {
            path: '/search/directors-ca',
            element: <SearchByDirectorPageCA />
        },
        {
            path: '/search/directors-ca-v2',
            element: <SearchByDirectorPageCAV2 />
        },
        {
            path: '/search/giving-history-ca',
            element: <SearchByGivingHistoryPageCA />
        },
        {
            path: '/search/giving-history-ca-v2',
            element: <SearchByGivingHistoryPageCAV2 />
        },
        {
            path: '/search/year-ca',
            element: <SearchByYearPageCA />
        },
        {
            path: '/search/year-ca-v2',
            element: <SearchByYearPageCAV2 />
        },
        {
            path: '/search/by-deadline',
            element: <SearchByDeadlinePage />
        },
        {
            path: '/search/by-international-funding',
            element: <SearchByInternacionalFundingPage />
        },
        {
            path: '/search/by-international-funding-v2',
            element: <SearchByInternacionalFundingPageV2 />
        },
        {
            path: '/foundation-profile-ca/:profileId',
            element: <FoundationProfileCaRedirect />
        },
        {
            path: '/foundation-profile-ca-v2/:profileId',
            element: <FoundationProfileCanadianV2 />
        },
        {
            path: '/document-generator/thank-you',
            element: <ThankYouLetterPage />
        },
        {
            path: '/document-generator/document-thank-you',
            element: <DocumentThankYouPage />
        },
        {
            path: '/document-generator/document-thank-you/funder/:profileId',
            element: <DocumentThankYouFunderPage />
        },
        {
            path: '/document-generator/impact-report',
            element: <ImpactReportLetterPage />
        },
        {
            path: '/document-generator/document-impact-report',
            element: <DocumentImpactReportPage />
        },
        {
            path: '/document-generator/document-impact-report/funder/:profileId',
            element: <DocumentImpactReportFunderPage />
        },
        {
            path: '/document-generator/document-optimization',
            element: <DocumentOptimizationPage />
        },
        {
            path: '/new-document-generator/:doctype',
            element: <NewDocumentGenerator />,
        },
        {
            path: '/new-document-generator/',
            element: <NewDocumentGenerator />,
        },
        {
            path: '/new-document-generator/document-builder/:doctype',
            element: <DocumentBuilder />
        },
        {
            path: '/new-document-generator/document-optimization',
            element: <NewDocumentOptimization />
        }
    ]
};

export default MainRoutes;
